import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
import styles from './AIIntelligenceCards.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExclamation } from '@fortawesome/free-solid-svg-icons';

const AIIntelligenceCards = ({ cards, title = "AI Claims Intelligence", sub_title = "AI-powered insights to guide your decision-making" }) => {
  const navigate = useNavigate();
  const basePath = process.env.PUBLIC_URL || '';

  const handleCardClick = (card) => {
    const cardCode = card.entityConfig?.code || 'STANDARD';
    navigate(`/ai-intelligence/${cardCode}`, { state: { card } });
  };
  const defaultCards = [
    {
      id: 3,
      entityId: "ai-card-claim-understanding-CLM-ID-0001",
      icon: "🗄️",
      title: "Claim Understanding Agent",
      subtitle: "Standardized Claim Snapshot Generated",
      buttonText: "View Claim Snapshot",
      moreInformation: {
        sourceInput: "### Inputs\n- Claim documents (Discharge summary, Invoice, Medical resume)\n- Policy & coverage context\n- Historical claims summary (if available)\n",
        logic: "### Validation logic\n- Extract patient/hospital/admission/diagnosis/procedure/invoice totals\n- Normalize dates into `DD Month YYYY`\n- Validate presence of minimum assessment fields\n- Produce a consistent structured snapshot for downstream checks\n",
        output: "### Output determination\n- Structured claim snapshot\n- Completeness score\n- Key extracted facts used by other cards\n"
      },
      confidencePercent: 90,
      keySignalsText: "- Diagnosis & procedure\n- Billing lines structured\n- Policy linked",
      metric: "100%",
      description: "Data Completeness",
      entityConfig: {
        id: 225,
        code: "AI_CARD_CLAIM_UNDERSTANDING",
        name: "AI Claim Understanding",
        displayName: "AI Claim Understanding",
        description: "AI Claim Understanding",
        type: "entity",
        status: "ACTIVE"
      }
    },
    {
      id: 5,
      entityId: "ai-card-policy-rules-CLM-ID-0001",
      icon: "🛡️",
      title: "Policy & Rules Insight Agent",
      subtitle: "Rule-Based Adjustment Applied",
      buttonText: "View Rule Impact",
      moreInformation: {
        sourceInput: "### Inputs\n- Policy coverage details (plan, deductible, co-pay)\n- Policy exclusions (if present)\n- Rule catalogue (admissibility rules)\n- Invoice categories/lines\n",
        logic: "### Validation logic\n- Determine applicable coverage + cost sharing\n- Apply admissibility rules to invoice categories/lines\n- Trigger rule IDs when conditions met (e.g., R-9.0)\n- Compute coverage-applied percentage (as defined by business rules)\n",
        output: "### Output determination\n- Coverage applied summary\n- Triggered rule references\n- Rule impact explanation (what got excluded and why)\n"
      },
      confidencePercent: 78,
      keySignalsText: "- Non-admissible items excluded\n- Rule R-9.0 triggered",
      metric: "80%",
      description: "Coverage Applied",
      insights: [
        "Non-admissible items excluded.",
        "Rule R-9.0 triggered."
      ],
      entityConfig: {
        id: 226,
        code: "AI_CARD_POLICY_RULES_INSIGHT",
        name: "AI Policy & Rules Insight",
        displayName: "AI Policy & Rules Insight",
        description: "AI Policy & Rules Insight",
        type: "entity",
        status: "ACTIVE"
      }
    },
    {
      id: 4,
      entityId: "ai-card-clinical-intelligence-CLM-ID-0001",
      icon: "🩺",
      title: "Clinical Intelligence Agent",
      subtitle: "Minor Clinical Variance",
      buttonText: "Review Clinical Signals",
      moreInformation: {
        sourceInput: "### Inputs\n- Primary & secondary diagnosis (ICD)\n- Procedure performed\n- Admission/discharge dates (LOS)\n- Medical resume narrative\n",
        logic: "### Validation logic\n- Map ICD/procedure to standard clinical pathway\n- Validate diagnosis–procedure alignment using clinical protocol library\n- Benchmark LOS vs pathway norms\n- Screen for overtreatment indicators based on patterns/thresholds\n",
        output: "### Output determination\n- Clinical alignment result (Valid/Invalid)\n- LOS evaluation label (normal/slightly elevated/high)\n- Clinical risk level (Low/Medium/High)\n"
      },
      confidencePercent: 88,
      keySignalsText: "- Diagnosis–Procedure aligned\n- LOS above benchmark\n- Pathway match: Valid",
      metric: "Low Risk Level",
      description: "Risk Level",
      riskLevel: "Low Risk Level",
      riskIcon: "↓",
      clinicalSignals: [
        "Diagnosis–Procedure aligned",
        "LOS above benchmark",
        "Pathway match: Valid"
      ],
      entityConfig: {
        id: 227,
        code: "AI_CARD_CLINICAL_INTELLIGENCE",
        name: "AI Clinical Intelligence",
        displayName: "AI Clinical Intelligence",
        description: "AI Clinical Intelligence",
        type: "entity",
        status: "ACTIVE"
      }
    },
    {
      id: 7,
      entityId: "ai-card-utilization-billing-CLM-ID-0001",
      icon: "📊",
      title: "Utilization & Billing Insight Agent",
      subtitle: "Bundling Deviation Detected",
      buttonText: "Inspect Billing Variance",
      caseId: "CLM-ID-0001",
      cardKey: "UTILIZATION_BILLING_INSIGHT",
      cardTitle: "Utilization & Billing Insight Agent",
      cardSubtitle: "Bundling Deviation Detected",
      statusLabel: "Analysis Completed",
      statusSeverity: "Moderate",
      primaryMetricLabel: "Variance Isolated",
      primaryMetricValueText: "Medium Risk Level",
      keySignalsText: "- Unbundled services detected\n- Variance isolated: IDR 23,300",
      confidencePercent: 70,
      evaluationTimestampText: "2023-10-17T15:45:00Z",
      ctaText: "Inspect Billing Variance",
      ctaAction: "open_review",
      metric: "Medium Risk Level",
      description: "Risk Level",
      riskLevel: "Medium Risk Level",
      riskIcon: "↓",
      detection: "Unbundled services detected.",
      varianceLabel: "Variance Isolated",
      varianceAmount: "IDR 23,300",
      moreInformation: {
        sourceInput: "### Inputs\n- Invoice line items (category, qty, unit price, amount)\n- Procedure performed (from discharge summary)\n- Reference bundling rules / typical package composition\n- Benchmark pricing (optional)\n",
        logic: "### Validation logic\n- Identify services expected to be bundled vs billed separately\n- Detect duplicate categories / suspicious quantities\n- Compare unit prices/amounts to benchmarks (if available)\n- Isolate variance amount attributable to anomalies\n",
        output: "### Output determination\n- Billing/utilization variance summary\n- Variance amount (e.g., IDR 23,300)\n- Rationale (unbundled services, extra charges, etc.)\n"
      },
      entityConfig: {
        id: 228,
        code: "AI_CARD_UTILIZATION_BILLING_INSIGHT",
        name: "AI Utilization & Billing Insight",
        displayName: "AI Utilization & Billing Insight",
        description: "AI Utilization & Billing Insight",
        type: "entity",
        status: "ACTIVE"
      }
    },
    {
      id: 2,
      entityId: "ai-card-claim-synthesis-CLM-ID-0001",
      icon: "🧠",
      title: "Claim Synthesis Agent",
      subtitle: "AI Recommendation: Adjust & Approve",
      buttonText: "Review AI Recommendation",
      moreInformation: {
        sourceInput: "### Inputs\n- Outputs from claim understanding, policy/rules, clinical, billing/utilization, risk/confidence\n- Claimed amount and admissibility decisions\n",
        logic: "### Validation logic\n- Consolidate adjustments from rule/billing checks\n- Ensure clinical validity and policy compliance\n- Resolve conflicts between signals (e.g., low clinical risk but billing anomalies)\n- Compute final payable and Identified saving figures\n",
        output: "### Output determination\n- Recommended decision label (e.g., Adjust & Approve)\n- Final payable amount\n- Identified saving amount and percentage\n- Explanation trace for assessor\n"
      },
      confidencePercent: 82,
      keySignalsText: "- Identified saving: IDR 23,300 (17%)\n- Final payable: IDR 104,500\n- Recommendation ready for review",
      metric: "IDR 104,500",
      description: "Final Payable",
      auditSavingLabel: "Identified saving",
      auditSavingAmount: "IDR 23,300",
      auditSavingPercentage: "(17%)",
      finalPayableLabel: "Final payable",
      finalPayableAmount: "IDR 104,500",
      entityConfig: {
        id: 229,
        code: "AI_CARD_CLAIM_SYNTHESIS",
        name: "AI Claim Synthesis",
        displayName: "AI Claim Synthesis",
        description: "AI Claim Synthesis",
        type: "entity",
        status: "ACTIVE"
      }
    },
    {
      id: 6,
      entityId: "ai-card-risk-confidence-CLM-ID-0001",
      icon: "📈",
      title: "Risk & Confidence Agent",
      subtitle: "Overall Risk Within Acceptable Threshold",
      buttonText: "View Risk Breakdown",
      moreInformation: {
        sourceInput: "### Inputs\n- Risk signals from all cards\n- Policy risk flags (if any)\n- Historical claim patterns (if any)\n- Billing/clinical variance indicators\n",
        logic: "### Validation logic\n- Score financial risk based on variance magnitude and rule triggers\n- Score clinical risk based on clinical alignment and LOS deviations\n- Score fraud risk based on patterns/overlaps/provider indicators (when available)\n- Combine into a single confidence score using weighted logic\n",
        output: "### Output determination\n- Overall confidence score (e.g., 92%)\n- Risk-level labels (Low/Medium/High)\n- Key signals supporting the confidence assessment\n"
      },
      confidencePercent: 92,
      keySignalsText: "- Financial risk: Controlled\n- Clinical risk: Low\n- Fraud indicators: None",
      metric: "92%",
      description: "Confidence Score",
      confidenceScore: "92%",
      confidenceLabel: "Confidence Score",
      riskSignals: [
        "Financial risk: Controlled",
        "Clinical risk: Low",
        "Fraud indicators: None"
      ],
      entityConfig: {
        id: 230, 
        code: "AI_CARD_RISK_CONFIDENCE", 
        name: "AI Risk & Confidence",
        displayName: "AI Risk & Confidence",
        description: "AI Risk & Confidence",
        type: "entity",
        status: "ACTIVE"
      }
    }
  ];

  // Merge API cards with default cards - always show all 6 cards
  const mergeCardsWithDefaults = () => {
    if (!cards || cards.length === 0) {
      // No API data, return all default cards with asterisk
      return defaultCards.map(card => ({
        ...card,
        title: `${card.title} *`,
        isDefault: true
      }));
    }

    // Create a map of API cards by their entityConfig.code
    const apiCardMap = {};
    cards.forEach(card => {
      const code = card.entityConfig?.code;
      if (code) {
        apiCardMap[code] = card;
      }
    });

    // Merge: use API card if available, otherwise use default with asterisk
    return defaultCards.map(defaultCard => {
      const code = defaultCard.entityConfig.code;
      const apiCard = apiCardMap[code];
      
      if (apiCard) {
        // API card exists, use it
        return {
          ...apiCard,
          isDefault: false
        };
      } else {
        // No API card, use default with asterisk
        return {
          ...defaultCard,
          title: `${defaultCard.title} *`,
          isDefault: true
        };
      }
    });
  };

  const cardsToRender = mergeCardsWithDefaults().filter(card => {
    // Filter out cards that have error messages
    return !card.rawApiData?.error_msg;
  });

  const renderCard = (card) => {
    const cardType = card.entityConfig?.code || card.template;
    
    switch (cardType) {
      case "AI_CARD_CLAIM_UNDERSTANDING":
      case "claimUnderstanding":
        const percentage = parseInt(card.metric) || 100;
        const circumference = 2 * Math.PI * 60;
        const offset = circumference - (percentage / 100) * circumference;
        
        return (
          <div key={card.id} className={styles.claimUnderstandingCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderIcon}>
                <img src={`${basePath}/assets/images/workbench/Claim_understanding.jpg`} alt="Claim Understanding" style={{ width: '30px', height: '30px' }} />
              </div>
              <div className={styles.cardHeaderContent}>
                <div className={styles.cardHeaderTitle}>{card.title}</div>
                <div className={styles.cardHeaderSubtitle}>
                  <img src={`${basePath}/assets/images/workbench/ai-star-icon-grey.png`} alt="subheader-icon" style={{ width: '16px', height: '16px' }} />
                  {card.subtitle}
                </div>
              </div>
            </div>
            
            <div className={styles.circularProgressContainer}>
              <div className={styles.circularProgressWrapper}>
                <svg width="100" height="100" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="43"
                    fill="none"
                    stroke="#E5E7EB"
                    strokeWidth="6"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="43"
                    fill="none"
                    stroke="#00a1ff"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    transform="rotate(-90 50 50)"
                    style={{ transition: 'stroke-dashoffset 1s ease' }}
                  />
                </svg>
                <div className={styles.circularProgressText}>
                  <span className={styles.circularProgressValue}>{card.metric}</span>
                </div>
                {percentage === 100 && (
                  <img 
                    src={`${basePath}/assets/images/workbench/Badge_Check.svg`} 
                    alt="Complete" 
                    style={{ 
                      width: '32px', 
                      height: '32px',
                      position: 'absolute',
                      top: '5px',
                      right: '0px'
                    }} 
                  />
                )}
              </div>
              <div className={styles.circularProgressLabel}>{card.description}</div>
            </div>
            
            <div className={styles.keySignalsSection}>
              <div className={styles.keySignalsTitle}>Key Signals</div>
              {card.keySignalsText && card.keySignalsText.split("- ").filter(signal => signal.trim()).map((signal, index) => (
                <div key={index} className={styles.keySignalItem}>
                  {signal.replace("✅ ", "✓ ")}
                </div>
              ))}
            </div>
            
            <button className={styles.viewButton} onClick={() => handleCardClick(card)}>{card.buttonText}</button>
          </div>
        );
      
      case "AI_CARD_POLICY_RULES_INSIGHT":
      case "policyRules":
        const policyPercentage = parseInt(card.metric) || 80;
        const policyCircumference = 2 * Math.PI * 60;
        const policyOffset = policyCircumference - (policyPercentage / 100) * policyCircumference;
        
        return (
          <div key={card.id} className={styles.policyRulesCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderIcon}>
                <img src={`${basePath}/assets/images/workbench/Policy.jpg`} alt="Policy Rules" style={{ width: '30px', height: '30px' }} />
              </div>
              <div className={styles.cardHeaderContent}>
                <div className={styles.cardHeaderTitle}>{card.title}</div>
                <div className={styles.cardHeaderSubtitle}>
                  <img src={`${basePath}/assets/images/workbench/ai-star-icon-grey.png`} alt="subheader-icon" style={{ width: '16px', height: '16px' }} />
                  {card.subtitle}
                </div>
              </div>
            </div>
            
            <div className={styles.circularProgressContainer}>
              <div className={styles.circularProgressWrapper}>
                <svg width="100" height="100" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="43"
                    fill="none"
                    stroke="#E5E7EB"
                    strokeWidth="6"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="43"
                    fill="none"
                    stroke="#00a1ff"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={policyCircumference}
                    strokeDashoffset={policyOffset}
                    transform="rotate(-90 50 50)"
                    style={{ transition: 'stroke-dashoffset 1s ease' }}
                  />
                </svg>
                <div className={styles.circularProgressText}>
                  <span className={styles.circularProgressValue}>{card.metric}</span>
                </div>
              </div>
              <div className={styles.circularProgressLabel}>{card.description}</div>
            </div>
            
            <div className={styles.keySignalsSection}>
              <div className={styles.keySignalsTitle}>Key Signals</div>
              {card.keySignalsText && card.keySignalsText.split("- ").filter(signal => signal.trim()).map((signal, index) => (
                <div key={index} className={styles.keySignalItem}>
                  {signal.replace("✅ ", "✓ ")}
                </div>
              ))}
            </div>
            
            <button className={styles.viewButton} onClick={() => handleCardClick(card)}>{card.buttonText}</button>
          </div>
        );
      
      case "AI_CARD_CLINICAL_INTELLIGENCE":
      case "clinicalIntelligence":
        const getClinicalSeverityIcon = (severity) => {
          const iconMap = {
            'success': <CheckCircle size={20} />,
            'warning': <FontAwesomeIcon icon={faExclamation} /> ,
            'error': <XCircle size={20} />,
            'info': <Info size={20} />
          };
          return iconMap[severity?.toLowerCase()] || <Info size={20} />;
        };

        const getClinicalSeverityColor = (severity) => {
          const colorMap = {
            'success': '#00b928',
            'warning': '#e8a900',
            'error': '#ff3e42',
            'info': '#00a1ff'
          };
          return colorMap[severity?.toLowerCase()] || '#00a1ff';
        };

        const clinicalSeverity = card.rawApiData?.statusSeverity;
        const clinicalColor = getClinicalSeverityColor(clinicalSeverity);

        return (
          <div key={card.id} className={styles.clinicalIntelligenceCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderIcon}>
                {/* <img src="/assets/images/workbench/Clinical_intelligence.jpg" alt="Clinical Intelligence" style={{ width: '30px', height: '30px' }} /> */}
                <img src={`${basePath}/assets/images/workbench/Clinical_intelligence.jpg`} alt="Clinical Intelligence" style={{ width: '30px', height: '30px' }} />

              </div>
              <div className={styles.cardHeaderContent}>
                <div className={styles.cardHeaderTitle}>{card.title}</div>
                <div className={styles.cardHeaderSubtitle}>
                    <img src={`${basePath}/assets/images/workbench/ai-star-icon-grey.png`} alt="subheader-icon" style={{ width: '16px', height: '16px' }} />
                  {card.subtitle}
                  </div>
              </div>
            </div>
            
            <div className={styles.riskBadgeSection} style={{ background: `linear-gradient(0deg, ${clinicalColor}15 0%, #ffffff 100%)` }}>
              <div className={styles.riskBadge} style={{ backgroundColor: clinicalColor, color: 'white' }}>
                {getClinicalSeverityIcon(clinicalSeverity)}
              </div>
              <div className={styles.riskLevel}>{card.rawApiData?.primaryMetricValueText}</div>
            </div>
            
            <div className={styles.clinicalSignalsSection}>
              <div className={styles.clinicalSignalsTitle}>Key Signals</div>
              {card.keySignalsText && card.keySignalsText.split("- ").map((signal, index) => (
                <div key={index} className={styles.clinicalSignalItem}>
                  {signal}
                </div>
              ))}
            </div>
            
            <button className={styles.viewButton} onClick={() => handleCardClick(card)}>{card.buttonText}</button>
          </div>
        );
      
      case "AI_CARD_UTILIZATION_BILLING_INSIGHT":
      case "utilizationBilling":
        const getUtilizationSeverityIcon = (severity) => {
          const iconMap = {
            'success': <CheckCircle size={20} />,
            'warning': <FontAwesomeIcon icon={faExclamation} />,
            'error': <XCircle size={20} />,
            'info': <Info size={20} />
          };
          return iconMap[severity?.toLowerCase()] || <Info size={20} />;
        };

        const getUtilizationSeverityColor = (severity) => {
          const colorMap = {
            'success': '#00b928',
            'warning': '#e8a900',
            'error': '#ff3e42',
            'info': '#00a1ff'
          };
          return colorMap[severity?.toLowerCase()] || '#00a1ff';
        };

        const utilizationSeverity = card.rawApiData?.statusSeverity;
        const utilizationColor = getUtilizationSeverityColor(utilizationSeverity);

        return (
          <div key={card.id} className={styles.utilizationBillingCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderIcon}>
                <img src={`${basePath}/assets/images/workbench/Utilization_billing.jpg`} alt="Utilization Billing" style={{ width: '30px', height: '30px' }} />
              </div>
              <div className={styles.cardHeaderContent}>
                <div className={styles.cardHeaderTitle}>
                  Utilization & Billing Insight Agent
                </div>
                <div className={styles.cardHeaderSubtitle}>
                  <img src={`${basePath}/assets/images/workbench/ai-star-icon-grey.png`} alt="subheader-icon" style={{ width: '16px', height: '16px' }} />
                  {card.cardSubtitle || card.subtitle}
                  
                </div>
              </div>
            </div>
            
            <div className={styles.mediumRiskBadgeSection} style={{ background: `linear-gradient(0deg, ${utilizationColor}15 0%, #ffffff 100%)` }}>
              <div className={styles.mediumRiskBadge} style={{ backgroundColor: utilizationColor, color: 'white' }}>
                {getUtilizationSeverityIcon(utilizationSeverity)}
              </div>
              <div className={styles.riskLevel}>{card.riskLevel || card.primaryMetricValueText || card.metric}</div>
            </div>
            
            {card.keySignalsText && (
              <div className={styles.keySignalsSection}>
                <div className={styles.keySignalsTitle}>Key Signals</div>
                {card.keySignalsText.split("- ").filter(item => item.trim()).map((signal, index) => (
                  <div key={index} className={styles.keySignalItem}>
                    {signal.trim()}
                  </div>
                ))}
              </div>
            )}
            
            {(card.varianceAmount || card.varianceLabel || card.description || card.rawApiData?.varianceAmount || card.rawApiData?.varianceLabel) && (
              <div className={styles.varianceBox}>
                {(card.varianceLabel || card.rawApiData?.varianceLabel) && (
                  <div className={styles.varianceLabel}>{card.varianceLabel || card.rawApiData?.varianceLabel}</div>
                )}
                <div className={styles.varianceAmount}>{card.varianceAmount || card.rawApiData?.varianceAmount || card.description}</div>
              </div>
            )}
            
            <button className={styles.viewButton} onClick={() => handleCardClick(card)}>{card.ctaText || card.buttonText}</button>
          </div>
        );
      
      case "AI_CARD_CLAIM_SYNTHESIS":
      case "claimSynthesis":
        const getSeverityClass = (severity) => {
          const severityMap = {
            'success': styles.statusSuccess,
            'warning': styles.statusWarning,
            'error': styles.statusError,
            'info': styles.statusInfo
          };
          return severityMap[severity?.toLowerCase()] || styles.statusInfo;
        };
        return (
          <div key={card.id} className={styles.claimSynthesisCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderIcon}>
                <img src={`${basePath}/assets/images/workbench/Claim_synthesis.jpg`} alt="Claim Synthesis" style={{ width: '30px', height: '30px' }} />
              </div>
              <div className={styles.cardHeaderContent}>
                <div className={styles.cardHeaderTitle}>{card.cardTitle || card.title}</div>
                <div className={styles.cardHeaderSubtitle}>
                  <img src={`${basePath}/assets/images/workbench/ai-star-icon-grey.png`} alt="subheader-icon" style={{ width: '16px', height: '16px' }} />
                  {card.cardSubtitle || card.subtitle}
                </div>
              </div>
            </div>
            
            
            <div className={styles.primaryMetricSection}>
              <div className={styles.primaryMetricLabel}>{card.primaryMetricLabel || "Final Payable"}</div>
              <div className={styles.primaryMetricValue}>{card.primaryMetricValueText || card.finalPayableAmount || card.metric}</div>
            </div>
            
            {card.keySignalsText && (
              <div className={styles.keySignalsSection}>
                <div className={styles.keySignalsTitle}>Key Signals</div>
                {card.keySignalsText.split('- ').map((signal, index) => (
                  signal.trim() && (
                    <div key={index} className={styles.keySignalItem}>
                      {signal}
                    </div>
                  )
                ))}
              </div>
            )}
            
            {card.rawApiData?.statusLabel && (
              <div className={styles.statusBadgeSection}>
                <div className={`${styles.statusBadge} ${getSeverityClass(card.rawApiData?.statusSeverity)}`}>
                  {card.rawApiData?.statusLabel}
                </div>
              </div>
            )}
            <button className={styles.viewButton} onClick={() => handleCardClick(card)}>{card.ctaText || card.buttonText}</button>
          </div>
        );
      
      case "AI_CARD_RISK_CONFIDENCE":
      case "riskConfidence":
        const confidencePercentage = parseInt(card.confidencePercent || card.confidenceScore) || 92;
        const confidenceCircumference = 2 * Math.PI * 60;
        const confidenceOffset = confidenceCircumference - (confidencePercentage / 100) * confidenceCircumference;
        
        return (
          <div key={card.id} className={styles.riskConfidenceCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderIcon}>
                <img src={`${basePath}/assets/images/workbench/Risk_confidence.jpg`} alt="Risk Confidence" style={{ width: '30px', height: '30px' }} />
              </div>
              <div className={styles.cardHeaderContent}>
                <div className={styles.cardHeaderTitle}>{card.cardTitle || card.title}</div>
                <div className={styles.cardHeaderSubtitle}>
                  <img src={`${basePath}/assets/images/workbench/ai-star-icon-grey.png`} alt="subheader-icon" style={{ width: '16px', height: '16px' }} />
                  {card.cardSubtitle || card.subtitle}
                </div>
              </div>
            </div>
            
            <div className={styles.circularProgressContainer}>
              <div className={styles.circularProgressWrapper}>
                <svg width="100" height="100" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="43"
                    fill="none"
                    stroke="#E5E7EB"
                    strokeWidth="6"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="43"
                    fill="none"
                    stroke="#00b928"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={confidenceCircumference}
                    strokeDashoffset={confidenceOffset}
                    transform="rotate(-90 50 50)"
                    style={{ transition: 'stroke-dashoffset 1s ease' }}
                  />
                </svg>
                <div className={styles.circularProgressText}>
                  {confidencePercentage === 100 ? (
                    <img src={`${basePath}/assets/images/workbench/Badge_Check.svg`} alt="Complete" style={{ width: '50px', height: '50px' }} />
                  ) : (
                    <span className={styles.circularProgressValue}>{card.primaryMetricValueText || card.confidenceScore || `${confidencePercentage}%`}</span>
                  )}
                </div>
              </div>
              <div className={styles.circularProgressLabel}>{card.primaryMetricLabel || card.confidenceLabel || card.description}</div>
            </div>
            
            {card.keySignalsText && (
              <div className={styles.keySignalsSection}>
                <div className={styles.keySignalsTitle}>Key Signals</div>
                {card.keySignalsText.split('\n').map((signal, index) => (
                  signal.trim() && (
                    <div key={index} className={styles.keySignalItem}>
                      {signal}
                    </div>
                  )
                ))}
              </div>
            )}
            
            <button className={styles.viewButton} onClick={() => handleCardClick(card)}>{card.ctaText || card.buttonText}</button>
          </div>
        );
      
      case "withMetric":
        return (
          <div key={card.id} className={styles.intelligenceCard}>
            <div className={styles.iconContainer}>{card.icon}</div>
            <div className={styles.cardTitle}>{card.title}</div>
            {card.metric && <div className={styles.cardMetric}>{card.metric}</div>}
            <div className={styles.cardDescription}>{card.description}</div>
          </div>
        );
      
      case "withoutMetric":
        return (
          <div key={card.id} className={styles.intelligenceCard}>
            <div className={styles.iconContainer}>{card.icon}</div>
            <div className={styles.cardTitle}>{card.title}</div>
            <div className={styles.cardDescription}>{card.description}</div>
          </div>
        );
      
      default:
        // Default template with metric
        return (
          <div key={card.id} className={styles.intelligenceCard}>
            <div className={styles.iconContainer}>{card.icon}</div>
            <div className={styles.cardTitle}>{card.title}</div>
            {card.metric && <div className={styles.cardMetric}>{card.metric}</div>}
            <div className={styles.cardDescription}>{card.description}</div>
          </div>
        );
    }
  };

  return (
    <div className={styles.intelligenceSection}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      <div className={styles.subSectionTitle}>{sub_title}</div>
      <div className={styles.intelligenceGrid}>
        {cardsToRender.map(card => renderCard(card))}
      </div>
    </div>
  );
};

export default AIIntelligenceCards;
